import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CredencialesSII } from 'src/app/credenciales-sii/credenciales-sii';
import {
  AccionPortal, ArchivoRechazado, Bloqueada, CesionPortalService, DocumentoCesion, EstadoCesionPortal,
  HitoCesion, Resultado, formatearFechaCesion,
} from './cesion-portal.service';

type TipoAviso = 'ok' | 'info' | 'error';

/**
 * La cesión de las facturas de una simulación desde el portal, sobre `/cliente/cesion-facturas/*`
 * (F-P2). Reemplaza «Ceder facturas» / «Ceder por Simpli» / «Ceder con AEC» cuando
 * `habilitada` es `true`; con el flag apagado no se muestra.
 *
 * Dos caminos, los dos terminan en «Ceder» (`ceder_aec`):
 * - **Ceder con mi certificado**: `buscar_dte` (el SII arma el AEC con el certificado de la
 *   empresa) → sondeo hasta que llegan los documentos → «Ceder».
 * - **Subir mis AEC**: `aec/` (carga parcial) → «Ceder».
 *
 * No hay websocket: mientras algo está en curso se relee el GET cada 5 s, con tope, y se deja de
 * leer al salir de la pantalla. Sólo se muestran las acciones que vienen en `acciones`.
 *
 * Se ve como el resto del portal: la tabla de «Resumen de tus facturas por ceder»
 * (`table-responsive-simpli` + `no-input`, paginada de a 7 con `numbered-pagination`), con una
 * fila expandible por factura para el mensaje, los hitos, los errores del AEC
 * y «Consultar al SII»; en ≤1024px, las cajas `box-factura` de `cliente-new-responsive`. Las acciones van
 * como píldoras `primary-button` / `secondary-button` abajo a la derecha.
 */
@Component({
  selector: 'app-cesion-portal',
  templateUrl: './cesion-portal.html',
  styleUrls: ['./cesion-portal.less'],
  standalone: false,
})
export class CesionPortalComponent implements OnInit, OnDestroy {

  private static readonly SONDEO_MS = 5000;
  // «Buscar DTE» trae todo de una vez al terminar la máquina; en dev y prod tardó minutos.
  private static readonly TOPE_BUSQUEDA_MS = 10 * 60 * 1000;
  // El SII puede tardar hasta 2 horas en responder una cesión (TIMEOUT_CESION del backend). No
  // se sondea tanto: pasado el tope se avisa y queda el botón «Actualizar».
  private static readonly TOPE_CESION_MS = 15 * 60 * 1000;

  @Input() id_sim: any;
  @Input() id_company: any = localStorage.getItem('company_id');
  /** Se emite cuando termina algo que pudo cambiar el estado de la simulación (cesión, búsqueda). */
  @Output() cambio = new EventEmitter<EstadoCesionPortal>();

  estado: EstadoCesionPortal | null = null;
  cargando = true;
  errorCarga: string | null = null;

  ejecutando: AccionPortal | 'clave' | null = null;
  aviso: { tipo: TipoAviso; texto: string } | null = null;
  bloqueadas: Bloqueada[] = [];
  rechazados: ArchivoRechazado[] = [];

  buscando = false;
  /** Se pidió «Ceder» en esta pantalla: al terminar se avisa el resultado. */
  private esperandoCesion = false;
  sondeoDetenido = false;
  confirmandoCesion = false;

  archivos: File[] = [];
  claveSii = '';
  claveIncorrecta = false;

  @ViewChild('inputAec') private inputAec?: ElementRef<HTMLInputElement>;

  /** Columnas de la tabla de escritorio; la fila de detalle (`expandido`) va aparte. */
  readonly columnas = ['factura', 'deudor', 'rut', 'monto', 'estado', 'detalle'];
  /** Igual que «Resumen de tus facturas por ceder»: de a 7. */
  readonly porPagina = 7;
  pagina = 1;
  detalleAbierto: number | null = null;


  private timer: any = null;
  private inicioSondeo = 0;
  private antesDeBuscar = new Map<number, string>();
  private destruido = false;

  constructor(private cesion: CesionPortalService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.cargar().then(() => {
      // Si se entra (o se vuelve) con una cesión en vuelo, se retoma el sondeo.
      if (this.cediendo) {
        this.empezarSondeo();
      }
    });
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.detenerSondeo();
  }

  /** Al volver a la pestaña se relee: el sondeo no corre con la pestaña oculta. */
  @HostListener('document:visibilitychange')
  alCambiarVisibilidad(): void {
    if (this.destruido || document.hidden || this.ejecutando) {
      return;
    }
    this.cargar(true).then(() => {
      if (this.enCurso && !this.timer) {
        this.empezarSondeo();
      }
    });
  }

  // ─── Derivados del estado ────────────────────────────────────────────────────────────────

  get documentos(): DocumentoCesion[] {
    return this.estado?.documentos || [];
  }

  get cediendo(): boolean {
    return this.documentos.some((d) => d.estado === 'CEDIENDO');
  }

  get enCurso(): boolean {
    return this.buscando || this.cediendo;
  }

  private con(accion: AccionPortal): DocumentoCesion[] {
    return this.documentos.filter((d) => (d.acciones || []).includes(accion));
  }

  get paraBuscar(): DocumentoCesion[] { return this.con('buscar_dte'); }
  get paraSubir(): DocumentoCesion[] { return this.con('subir_aec'); }
  get paraCeder(): DocumentoCesion[] { return this.con('ceder_aec'); }

  get tieneCertificado(): boolean { return !!this.estado?.credenciales?.tiene_certificado; }
  get tieneClaveSii(): boolean { return !!this.estado?.credenciales?.tiene_clave_sii; }
  get esNubox(): boolean { return !!this.estado?.metodos?.facturador_nubox; }

  /**
   * «Ceder con mi certificado» se ofrece si el backend ofrece `buscar_dte`, o si no lo ofrece
   * sólo porque falta el certificado (sin certificado `buscar_dte` no viene en `acciones`).
   */
  get mostrarCertificado(): boolean {
    if (this.paraBuscar.length) {
      return true;
    }
    return !this.tieneCertificado && !this.esNubox && this.paraSubir.length > 0;
  }

  get mostrarSubir(): boolean {
    return this.paraSubir.length > 0;
  }

  get todasCedidas(): boolean {
    return this.documentos.length > 0 && this.documentos.every((d) => d.estado === 'CEDIDA');
  }

  /** La página vigente, acotada: el GET puede traer menos facturas que antes. */
  get paginaActual(): number {
    const paginas = Math.max(1, Math.ceil(this.documentos.length / this.porPagina));
    return Math.min(Math.max(1, this.pagina), paginas);
  }

  get documentosPagina(): DocumentoCesion[] {
    const inicio = (this.paginaActual - 1) * this.porPagina;
    return this.documentos.slice(inicio, inicio + this.porPagina);
  }

  cambiarPagina(pagina: number): void {
    this.pagina = pagina;
  }

  alternarDetalle(doc: DocumentoCesion): void {
    const id = doc.documento_factura_id;
    this.detalleAbierto = this.detalleAbierto === id ? null : id;
  }


  /** La acción que va como `primary-button`; el resto, `secondary-button`. */
  get principal(): 'ceder' | 'subir' | 'buscar' | 'certificado' | null {
    if (this.archivos.length) {
      return 'subir';
    }
    if (this.paraCeder.length && !this.cediendo) {
      return 'ceder';
    }
    if (this.mostrarCertificado && !this.enCurso) {
      if (!this.tieneCertificado) {
        return 'certificado';
      }
      if (this.paraBuscar.length) {
        return 'buscar';
      }
    }
    return this.mostrarSubir && !this.enCurso ? 'subir' : null;
  }

  get hayAcciones(): boolean {
    return (this.paraCeder.length > 0 && !this.cediendo)
      || (!this.enCurso && (this.mostrarSubir || (this.mostrarCertificado && (!this.tieneCertificado || this.paraBuscar.length > 0))));
  }

  /** Los avisos con las clases globales del portal (`styles.scss`). */
  claseAviso(tipo: TipoAviso): string {
    return tipo === 'ok' ? 'success-msg' : (tipo === 'error' ? 'error-msg' : 'info-msg');
  }

  claseEstado(estado: string): string {
    switch (estado) {
      case 'CEDIDA':
        return 'estado-ok';
      case 'LISTA_AEC':
      case 'LISTA_DTE':
        return 'estado-lista';
      case 'CEDIENDO':
        return 'estado-curso';
      case 'SIN_RESPUESTA':
      case 'YA_CEDIDA':
        return 'estado-alerta';
      case 'RECHAZADA':
      case 'AEC_CON_ERRORES':
      case 'CEDIDA_A_OTRO':
        return 'estado-error';
      default:
        return 'estado-neutro';
    }
  }

  iconoEstado(estado: string): string {
    switch (estado) {
      case 'CEDIDA':
        return 'check_circle';
      case 'LISTA_AEC':
      case 'LISTA_DTE':
        return 'task';
      case 'CEDIENDO':
        return 'progress_activity';
      case 'SIN_RESPUESTA':
      case 'YA_CEDIDA':
        return 'schedule';
      case 'RECHAZADA':
      case 'AEC_CON_ERRORES':
      case 'CEDIDA_A_OTRO':
        return 'cancel';
      default:
        return 'description';
    }
  }

  /** El mismo formato que las tablas del portal: sin decimales, con punto de miles. */
  formatMoney(amount: any): string {
    const n = Math.round(Math.abs(Number(amount) || 0));
    const signo = Number(amount) < 0 ? '-' : '';
    return signo + n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  // ─── Carga y sondeo ──────────────────────────────────────────────────────────────────────

  async cargar(silencioso: boolean = false): Promise<void> {
    if (!silencioso) {
      this.cargando = true;
    }
    const r = await this.cesion.estado(this.id_sim);
    if (this.destruido) {
      return;
    }
    if (r.status === 200 && r.body.documentos) {
      this.aplicarEstado(r.body as EstadoCesionPortal);
      this.errorCarga = null;
    } else if (!silencioso || !this.estado) {
      this.errorCarga = this.textoError(r);
    }
    this.cargando = false;
  }

  private aplicarEstado(body: Partial<EstadoCesionPortal>): void {
    if (!body.documentos) {
      return;
    }
    this.estado = {
      resumen: body.resumen || { total: body.documentos.length, por_estado: [] },
      metodos: body.metodos || { buscar_dte: false, facturador_nubox: false },
      credenciales: body.credenciales || { tiene_certificado: false, tiene_clave_sii: false },
      documentos: body.documentos,
    };
    if (!this.paraCeder.length) {
      this.confirmandoCesion = false;
    }
  }

  private empezarSondeo(): void {
    this.sondeoDetenido = false;
    this.inicioSondeo = Date.now();
    this.programarSondeo();
  }

  private programarSondeo(): void {
    if (this.timer || this.destruido) {
      return;
    }
    this.timer = setTimeout(() => this.tick(), CesionPortalComponent.SONDEO_MS);
  }

  private detenerSondeo(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private async tick(): Promise<void> {
    this.timer = null;
    if (this.destruido) {
      return;
    }
    // Con la pestaña oculta no se pide nada; al volver, `alCambiarVisibilidad` relee.
    if (!document.hidden && !this.ejecutando) {
      await this.cargar(true);
      if (this.destruido) {
        return;
      }
    }
    const transcurrido = Date.now() - this.inicioSondeo;

    if (this.buscando) {
      if (this.terminoLaBusqueda()) {
        this.buscando = false;
        this.avisarFinBusqueda();
      } else if (transcurrido > CesionPortalComponent.TOPE_BUSQUEDA_MS) {
        this.buscando = false;
        this.avisar('info', 'La búsqueda en el SII está tardando más de lo normal. Vuelve en unos minutos o pulsa «Actualizar».');
      }
    }

    if (!this.enCurso) {
      if (this.esperandoCesion) {
        this.esperandoCesion = false;
        this.avisarFinCesion();
      }
      this.cambio.emit(this.estado!);
      return;
    }

    if (this.cediendo && !this.buscando && transcurrido > CesionPortalComponent.TOPE_CESION_MS) {
      this.sondeoDetenido = true;
      this.avisar('info', 'El SII todavía no responde la cesión. Puede tardar hasta 2 horas: vuelve más tarde o pulsa «Actualizar».');
      return;
    }
    this.programarSondeo();
  }

  /** «Buscar DTE» deja todo junto al terminar: basta con que una factura haya cambiado. */
  private terminoLaBusqueda(): boolean {
    return this.documentos.some((d) => {
      const antes = this.antesDeBuscar.get(d.documento_factura_id);
      return antes !== undefined && antes !== this.firma(d);
    });
  }

  private firma(d: DocumentoCesion): string {
    return `${d.estado}|${d.marcas?.tiene_aec}|${d.marcas?.tiene_dte}`;
  }

  async actualizar(): Promise<void> {
    await this.cargar(true);
    if (this.enCurso && !this.timer) {
      this.empezarSondeo();
    } else if (!this.enCurso) {
      this.sondeoDetenido = false;
    }
  }

  // ─── Acciones ────────────────────────────────────────────────────────────────────────────

  async buscarDocumentos(): Promise<void> {
    await this.ejecutar('buscar_dte');
  }

  pedirConfirmacionCesion(): void {
    if (!this.tieneClaveSii) {
      this.avisar('error', 'Para ceder necesitamos la clave tributaria (SII) de la empresa. Ingrésala más abajo y vuelve a intentar.');
      return;
    }
    this.confirmandoCesion = true;
  }

  async ceder(): Promise<void> {
    this.confirmandoCesion = false;
    await this.ejecutar('ceder_aec');
  }

  validandoId: number | null = null;

  async validar(doc: DocumentoCesion): Promise<void> {
    this.validandoId = doc.documento_factura_id;
    try {
      await this.ejecutar('validar', [doc.documento_factura_id]);
    } finally {
      this.validandoId = null;
    }
  }

  private async ejecutar(accion: Exclude<AccionPortal, 'subir_aec'>, documentos?: number[]): Promise<void> {
    if (this.ejecutando) {
      return;
    }
    this.ejecutando = accion;
    this.aviso = null;
    this.bloqueadas = [];
    this.rechazados = [];
    const antes = new Map(this.documentos.map((d) => [d.documento_factura_id, this.firma(d)]));

    let r: Resultado;
    try {
      r = await this.cesion.accion(this.id_sim, accion, documentos);
    } finally {
      this.ejecutando = null;
    }
    if (this.destruido) {
      return;
    }
    this.aplicarEstado(r.body);
    this.bloqueadas = r.body.bloqueadas || [];
    const codigo = r.body.codigo || null;

    if (r.status === 202) {
      if (accion === 'buscar_dte') {
        this.antesDeBuscar = antes;
        this.buscando = true;
        this.avisar('info', 'Estamos buscando tus documentos en el SII con tu certificado. Puede tardar unos minutos: esta pantalla se actualiza sola.');
      } else {
        this.esperandoCesion = true;
        this.avisar('info', `Estamos cediendo tus facturas a Simpli. ${r.body.message || ''} El SII puede tardar en responder: esta pantalla se actualiza sola.`.replace(/\s+/g, ' ').trim());
      }
      this.empezarSondeo();
      return;
    }

    if (r.status === 200) {
      if (codigo === 'SIN_PENDIENTES') {
        this.avisar('ok', 'Ya tenemos los documentos de todas tus facturas: puedes cederlas.');
      } else {
        this.avisar('ok', r.body.message || 'Listo.');
      }
      this.cambio.emit(this.estado!);
      return;
    }

    // Errores: se explica, y si algo quedó en curso se sigue mirando.
    if (codigo === 'SIN_CLAVE_SII' && this.estado) {
      this.estado.credenciales.tiene_clave_sii = false;
    }
    if (codigo === 'SIN_CERTIFICADO' && this.estado) {
      this.estado.credenciales.tiene_certificado = false;
    }
    if (codigo === 'BUSQUEDA_EN_CURSO') {
      this.antesDeBuscar = antes;
      this.buscando = true;
      this.avisar('info', 'Ya estamos buscando tus documentos en el SII. Esta pantalla se actualiza sola.');
      this.empezarSondeo();
      return;
    }
    if (codigo === 'CESION_YA_EN_CURSO') {
      this.esperandoCesion = true;
      this.avisar('info', 'Ya hay una cesión en curso para estas facturas. Esta pantalla se actualiza sola.');
      this.empezarSondeo();
      return;
    }
    this.avisar('error', this.textoError(r));
  }

  // ─── Subir AEC ───────────────────────────────────────────────────────────────────────────

  elegirArchivos(): void {
    this.inputAec?.nativeElement.click();
  }

  alElegirArchivos(event: any): void {
    const elegidos: File[] = Array.from(event?.target?.files || []);
    const xml = elegidos.filter((f) => f.name.toLowerCase().endsWith('.xml'));
    if (xml.length < elegidos.length) {
      this.avisar('error', 'Sólo se pueden subir archivos AEC en formato .xml.');
    }
    const nombres = new Set(this.archivos.map((f) => f.name));
    this.archivos = [...this.archivos, ...xml.filter((f) => !nombres.has(f.name))];
    if (event?.target) {
      event.target.value = '';
    }
  }

  quitarArchivo(archivo: File): void {
    this.archivos = this.archivos.filter((f) => f !== archivo);
  }

  async subirAec(): Promise<void> {
    if (!this.archivos.length || this.ejecutando) {
      return;
    }
    this.ejecutando = 'subir_aec';
    this.aviso = null;
    this.bloqueadas = [];
    this.rechazados = [];
    let r: Resultado;
    try {
      r = await this.cesion.subirAec(this.id_sim, this.archivos);
    } finally {
      this.ejecutando = null;
    }
    if (this.destruido) {
      return;
    }
    this.aplicarEstado(r.body);
    if (r.status === 200) {
      this.archivos = [];
      this.rechazados = r.body.archivos_rechazados || [];
      const aceptados = r.body.archivos_aceptados || 0;
      const recibidos = r.body.archivos_recibidos || 0;
      if (this.rechazados.length) {
        this.avisar(aceptados ? 'info' : 'error',
          `Recibimos ${recibidos} archivo(s): ${aceptados} aceptado(s) y ${this.rechazados.length} con problemas (detalle abajo).`);
      } else {
        this.avisar('ok', `Recibimos ${aceptados} AEC. Ya puedes ceder las facturas.`);
      }
      this.cambio.emit(this.estado!);
      return;
    }
    this.avisar('error', this.textoError(r));
  }

  // ─── Credenciales ────────────────────────────────────────────────────────────────────────

  cargarCertificado(): void {
    const dialogRef = this.dialog.open(CredencialesSII, { width: '600px', maxWidth: '95vw', autoFocus: false });
    dialogRef.componentInstance.id_company = this.id_company;
    dialogRef.componentInstance.token = localStorage.getItem('token');
    dialogRef.componentInstance.id_sim = this.id_sim;
    dialogRef.afterClosed().subscribe(() => this.cargar(true));
  }

  async guardarClave(): Promise<void> {
    if (!this.claveSii || this.ejecutando) {
      return;
    }
    this.ejecutando = 'clave';
    this.claveIncorrecta = false;
    let r: Resultado;
    try {
      r = await this.cesion.guardarClaveSii(this.id_company, this.claveSii);
    } finally {
      this.ejecutando = null;
    }
    if (this.destruido) {
      return;
    }
    if (r.status === 200 && r.body.data?.password_valid === false) {
      this.claveIncorrecta = true;
      return;
    }
    if (r.status !== 200) {
      this.avisar('error', r.status === 403
        ? 'Sólo el titular de la empresa puede guardar la clave tributaria.'
        : 'No pudimos validar la clave con el SII. Intenta de nuevo en unos minutos.');
      return;
    }
    this.claveSii = '';
    await this.cargar(true);
    this.avisar('ok', 'Guardamos la clave tributaria de la empresa. Ya puedes ceder.');
  }

  // ─── Hitos ───────────────────────────────────────────────────────────────────────────────
  // El portal no muestra el historial técnico de la cesión (decisión del 26-oct-2026): sólo los
  // tres hitos que arma el backend. Cada hito lleva siempre su ícono, como los pasos de
  // «Detalle general / Cesión / Estado final»; el estado lo dicen el círculo y el color
  // (ver `.cp-hito-*` en el less). Sólo el error cambia de ícono: la X del «No cedida» de la tabla.

  iconoHito(h: HitoCesion): string {
    if (h.estado === 'error') {
      return 'close';
    }
    switch (h.clave) {
      case 'documentos':
        return 'description';
      case 'envio':
        return 'send';
      default:
        return 'handshake';
    }
  }

  /** `dd/MM/yyyy HH:mm` en hora de Chile: ver `formatearFechaCesion`. */
  formatearFecha(fecha: string | null | undefined): string {
    return formatearFechaCesion(fecha);
  }

  // ─── Mensajes ────────────────────────────────────────────────────────────────────────────

  private avisar(tipo: TipoAviso, texto: string): void {
    this.aviso = { tipo, texto };
  }

  private avisarFinBusqueda(): void {
    const listas = this.paraCeder.length;
    if (listas) {
      this.avisar('ok', `Encontramos los documentos en el SII: ${listas} factura(s) lista(s) para ceder.`);
    } else {
      this.avisar('info', 'Terminamos de buscar en el SII. Revisa el estado de cada factura más abajo.');
    }
  }

  private avisarFinCesion(): void {
    if (this.todasCedidas) {
      this.avisar('ok', '¡Listo! El SII registró la cesión de todas tus facturas.');
    } else {
      this.avisar('info', 'El SII respondió. Revisa el resultado de cada factura más abajo.');
    }
  }

  /** El texto para el cliente según el `codigo` (ver la tabla de `api.md`). */
  private textoError(r: Resultado): string {
    const codigo = r.body.codigo;
    switch (codigo) {
      case 'SIN_CLAVE_SII':
        return 'Falta la clave tributaria (SII) de la empresa. Ingrésala más abajo y vuelve a intentar.';
      case 'SIN_CERTIFICADO':
        return 'Falta tu certificado digital. Cárgalo y vuelve a intentar.';
      case 'FACTURADOR_NUBOX':
        return 'Tu empresa factura con Nubox: no podemos buscar tus documentos en el SII. Sube tus AEC o contacta a tu ejecutivo.';
      case 'SII_API_NO_RESPONDE':
        return 'No pudimos comunicarnos con el SII. Intenta de nuevo en unos minutos.';
      case 'CESIONARIO_INVALIDO':
        return 'Hay un problema de configuración de nuestro lado. Contacta a tu ejecutivo.';
      case 'NO_SE_PUDO_INICIAR_LA_CESION':
        return 'No pudimos iniciar la cesión. Intenta de nuevo en unos minutos.';
      case 'SIN_ARCHIVOS':
        return 'Elige al menos un archivo AEC (.xml).';
      case 'AEC_NO_ENCONTRADO':
        return 'Esa factura ya no tiene el AEC que se intentó validar.';
    }
    if (r.status === 409) {
      return r.body.message || 'No hay nada que hacer ahora con esas facturas: ya se están procesando o ya fueron cedidas.';
    }
    if (r.status === 403) {
      return 'Sólo el titular de la empresa puede ceder las facturas de esta operación.';
    }
    if (r.status === 404) {
      return 'Esta opción no está disponible en este momento. Contacta a tu ejecutivo.';
    }
    if (r.status === 0) {
      return 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.';
    }
    if (r.status >= 500) {
      return 'Ocurrió un error de nuestro lado. Intenta de nuevo en unos minutos.';
    }
    return r.body.message || 'Ocurrió un error, intenta de nuevo.';
  }
}
